import type { Graph, Figure } from "@hydrologiq/schemas/1.0.0/installationPage"
import type { SystemBasic } from "../../types/SystemTypes"
import type { SystemViewData } from "../../types/SystemView"

/**
 * Determine whether a physical system has hydrogen capability.
 *
 * Checks physical topology (node types, asset categories, and equipment kinds),
 * explicit hydrogen graph configurations, and Timescale hydrogen telemetry fields.
 *
 * NOTE: Liquid storage tanks (diesel, water, wastewater, adblue) have volume/capacity
 * and must NEVER be identified as hydrogen systems.
 */
export const isHydrogenSystem = (
  systemViewData?: SystemViewData,
  info?: SystemBasic
): boolean => {
  // 1. Physical topology: check if any node or asset has hydrogen characteristics
  const allNodes = [
    ...(systemViewData?.installations?.flatMap((g) => g.nodes ?? []) ?? []),
    ...(systemViewData?.isolatedNodes ?? []),
  ]

  for (const node of allNodes) {
    const nodeType = (node.type || "").toLowerCase()
    if (
      nodeType === "hydrogen_producer" ||
      nodeType === "electrolyser" ||
      nodeType === "refueling_bowser" ||
      nodeType === "fuel_cell"
    ) {
      return true
    }

    const asset = node.asset as any
    if (!asset) continue

    const category = (asset.category || "").toLowerCase()
    const kind = (asset.kind || "").toLowerCase()
    const name = (asset.name || "").toLowerCase()

    if (category === "hydrogen" || name.includes("hydrogen")) {
      return true
    }

    if (
      kind === "tube_trailer" ||
      kind === "manifold_cylinder_pallet" ||
      kind === "gas_management" ||
      kind === "fuel_cell" ||
      kind === "electrolyser"
    ) {
      return true
    }

    // Hydrogen gas pressure vessels (compressed hydrogen)
    // NOTE: Liquid fuel tanks (diesel/water/adblue) must NOT be treated as hydrogen
    if (
      asset.min_pressure != null &&
      category !== "diesel" &&
      category !== "water" &&
      category !== "adblue"
    ) {
      return true
    }
  }

  // 2. Explicit graphs configured with hydrogen fields
  if (info?.graphs && info.graphs.length > 0) {
    const hasH2Graph = info.graphs.some(
      (g) =>
        g.source?.field === "weight" ||
        g.source?.field === "pressure_manifold" ||
        (g as any).filter?.category === "hydrogen" ||
        Boolean(g.title?.toLowerCase().includes("hydrogen"))
    )
    if (hasH2Graph) return true
  }

  // 3. Timescale telemetry fields: weight or pressure_manifold indicates hydrogen telemetry
  const timescaleFields = systemViewData?.system?.timescaleFields ?? []
  if (
    timescaleFields.some(
      (tf) => tf.field === "weight" || tf.field === "pressure_manifold"
    )
  ) {
    return true
  }

  return false
}

/**
 * Identify if a figure is hydrogen-specific.
 */
export const isHydrogenFigure = (f: Figure): boolean => {
  return (
    f.type === "HydrogenFuelForecasting" ||
    f.type === "HydrogenFuelLevel" ||
    f.type === "WeightDifference" ||
    f.type === "VehiclesToFill" ||
    f.type === "ElectrolyserStatus" ||
    f.type === "HoursToFill" ||
    f.type === "HydrogenProduced" ||
    Boolean(f.title?.toLowerCase().includes("hydrogen"))
  )
}

/**
 * Identify if a graph is hydrogen-specific.
 */
export const isHydrogenGraph = (g: Graph): boolean => {
  return (
    g.source?.field === "weight" ||
    g.source?.field === "pressure_manifold" ||
    (g as any).filter?.category === "hydrogen" ||
    Boolean(g.title?.toLowerCase().includes("hydrogen")) ||
    Boolean(g.title?.toLowerCase().includes("pressure"))
  )
}
